import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Empty, Input, Skeleton, message } from 'antd';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import ColumnHeader from './ColumnHeader';
import { WMS_DAILY_TASK_MONITORING_PATH, WMS_NO_UPDATE_WAREHOUSE_PATH } from '../../routePaths';
import { WorkbenchAlert, WorkbenchAlerts, MetricTrendPoint } from '../types';
import {
  composeAlertDriverScope,
  composeAlertHandleAction,
  composeAlertHeadline,
  composeAlertLabel,
  composeAlertRecommendedAction,
  composeAlertValue,
} from '../utils/alertTextComposer';
import { formatLocalDateTime } from '../utils/taskTextComposer';
import { MARK_WORKBENCH_ALERT_HANDLED } from '../graphql/mutations/workbenchMutations';
import AlertEntityRow from './AlertEntityRow';
import NotifyDspButton from './NotifyDspButton';
import useHandledAlertPaging from '../hooks/useHandledAlertPaging';

/**
 * 时刻相同时的次序，与 PRD 的复盘顺序一致。周期复盘卡由同一个批处理生成，
 * 一批卡的时刻常常完全相同，此时仍按类型出，避免同一批的顺序在两次刷新间跳动。
 * 未收录的类型排在其后。
 */
const TYPE_ORDER = ['LOST', 'COMPLAINT', 'RETURN_RATE', 'NO_UPDATE_72H'];

/**
 * 「查看详情」的落地页。丢件 / 投诉 / 退件都是当日派送口径，落到当日派送进展；
 * 断更落到看板的断更明细，与任务栏断更任务的入口保持一致。
 * 未收录的类型不给按钮 —— 跳到猜的页面比不跳更糟。
 */
const DETAIL_PATH_BY_TYPE: Record<string, string> = {
  LOST: WMS_DAILY_TASK_MONITORING_PATH,
  COMPLAINT: WMS_DAILY_TASK_MONITORING_PATH,
  RETURN_RATE: WMS_DAILY_TASK_MONITORING_PATH,
  NO_UPDATE_72H: WMS_NO_UPDATE_WAREHOUSE_PATH,
};

/**
 * 待处理按发现时刻倒序、已关闭按处置时刻倒序，最近的排最前；无时刻的垫底。
 *
 * 已关闭的时刻优先取留言时刻（留言可改，是最新的），其次是处置动作本身的时刻，
 * 最后回落到上游的结案时刻 ——「已约谈 DSP」这类动作不产生留言，只有后两者。
 *
 * 不复用 `byHandledAtDesc`：那是日报/片区风险列表的口径（只有处置时刻一种语义），
 * 这里要按 tab 切换排序字段，塞进去会让两处互相牵制。
 */
const sortForTab = (alerts: WorkbenchAlert[], isOpen: boolean): WorkbenchAlert[] => {
  const rank = (code?: string | null) => {
    const index = code ? TYPE_ORDER.indexOf(code) : -1;
    return index === -1 ? TYPE_ORDER.length : index;
  };
  const timeOf = (alert: WorkbenchAlert) =>
    (isOpen
      ? alert.firstDetectedAt
      : alert.comment?.at ?? alert.handledAt ?? alert.resolvedAt) ?? '';

  return [...alerts].sort((a, b) => {
    const [at, bt] = [timeOf(a), timeOf(b)];
    if (!at || !bt) {
      if (at !== bt) return Number(!at) - Number(!bt);
    } else if (at !== bt) {
      return bt.localeCompare(at);
    }
    return rank(a.alertTypeCode) - rank(b.alertTypeCode);
  });
};

interface PeriodReviewSectionProps {
  data: WorkbenchAlerts;
  warehouseId?: number;
  timeZone?: string | null;
  loading?: boolean;
  onCommentSaved?: () => void;
}

const MiniBars: React.FC<{ trend: MetricTrendPoint[] }> = ({ trend }) => {
  if (trend.length === 0) return null;
  const max = Math.max(...trend.map((p) => p.value));

  return (
    <div className="flex items-end gap-0.5 h-8 shrink-0">
      {trend.map((point, idx) => {
        const height = max > 0 ? (point.value / max) * 100 : 0;
        // 末位加深，凸显「最近一期」
        const isLast = idx === trend.length - 1;
        return (
          <div
            key={idx}
            className={`w-1.5 rounded-sm ${isLast ? 'bg-violet-600' : 'bg-violet-300'}`}
            style={{ height: `${Math.max(height, 12)}%` }}
            title={`${point.label}: ${point.value}`}
          />
        );
      })}
    </div>
  );
};

const ReviewCard: React.FC<{
  alert: WorkbenchAlert;
  warehouseId?: number;
  timeZone?: string | null;
  onCommentSaved?: () => void;
}> = ({ alert, warehouseId, timeZone, onCommentSaved }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');
  // 留言框只在点了处置按钮后展开：卡片默认态是「看结论」，常驻输入框会把复盘看成待填表单
  const [commenting, setCommenting] = useState(false);
  const [markHandled, { loading: saving }] = useMutation(MARK_WORKBENCH_ALERT_HANDLED);

  // 处置走 markWorkbenchAlertHandled（上游 completeTaskManually）而非只存留言：
  // 「已抽查线路」是结案动作，复盘项要随之进「已关闭」，仅存留言不会改变状态
  const handleSubmitComment = async () => {
    if (!warehouseId) return;
    try {
      const { data } = await markHandled({
        variables: {
          warehouseId,
          alertId: alert.alertId,
          actionCode: null,
          actionTaken: draft.trim() || null,
        },
      });
      // 上游对重复提交做状态守卫返回 false，这不是错误
      if (data?.markWorkbenchAlertHandled) {
        message.success(String(t('workbench.markedHandled')));
      } else {
        message.info(String(t('workbench.alertText.alreadyHandled')));
      }
      setDraft('');
      setCommenting(false);
      onCommentSaved?.();
    } catch (e) {
      message.error(String(t('workbench.alertText.handleFailed')));
    }
  };

  const label = composeAlertLabel(alert);
  const value = composeAlertValue(alert);
  const headline = composeAlertHeadline(alert);
  const driverScope = composeAlertDriverScope(alert);
  const recommendedAction = composeAlertRecommendedAction(alert);
  const detailPath = alert.alertTypeCode ? DETAIL_PATH_BY_TYPE[alert.alertTypeCode] : undefined;
  // 有 DSP 明细的卡片：处置动作就是约谈这些 DSP，故底部按钮直接发 Lark 而不是走「标记已处理」。
  // 投诉卡明细是司机行，但 PRD 的处置动作同为「已约谈 DSP」，故一并走发送。
  // 不传 dspId —— 上游按 taskId 会给该任务下所有 DSP 发消息，卡上有几家就发几家
  const hasDspEntity =
    alert.alertTypeCode === 'COMPLAINT' ||
    alert.entities.some((entity) => entity.entityType === 'DSP');

  return (
    <div className="border border-violet-200 rounded-lg p-4">
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-semibold text-violet-600 min-w-0">{label}</h4>
        {alert.periodLabel && (
          <span className="text-xs text-gray-400 shrink-0">{alert.periodLabel}</span>
        )}
      </div>

      <div className="flex items-end justify-between gap-3 mt-2">
        <div className="min-w-0">
          <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
          <p className="text-xs text-gray-500 mt-1">
            {headline}
            {driverScope && ` · ${driverScope}`}
          </p>
        </div>
        <MiniBars trend={alert.trend} />
      </div>

      {alert.entities.length > 0 && (
        <div className="mt-3 divide-y divide-violet-100">
          {alert.entities.map((entity) => (
            <AlertEntityRow
              key={`${entity.entityType}-${entity.entityId}`}
              alert={alert}
              entity={entity}
            />
          ))}
        </div>
      )}

      {recommendedAction && (
        <div className="mt-3 bg-gray-50 rounded-md px-3 py-2">
          <p className="text-xs font-semibold text-violet-600">
            {String(t('workbench.recommendedAction'))}
          </p>
          <p className="text-sm text-gray-700 mt-1">{recommendedAction}</p>
        </div>
      )}



      {alert.resolvedAt ? (
        alert.comment && (
          <div className="mt-3 border-l-2 border-violet-200 pl-3">
            <p className="text-sm text-gray-600 italic">“{alert.comment.content}”</p>
            <p className="text-xs text-gray-400 mt-1">
              {alert.comment.byName} {formatLocalDateTime(alert.comment.at, timeZone) || '—'}
            </p>
          </div>
        )
      ) : commenting ? (
        <div className="mt-3">
          <Input.TextArea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={String(t('workbench.addCommentPlaceholder'))}
          />
          {/* 显式按钮而非 onBlur 提交：失焦即写库会让仓管误触，且无法区分「写完了」与「点去别处」 */}
          <div className="flex items-center gap-2 mt-2">
            <Button size="small" type="primary" loading={saving} onClick={handleSubmitComment}>
              {String(t('workbench.alertText.saveComment'))}
            </Button>
            <Button
              size="small"
              disabled={saving}
              onClick={() => {
                setCommenting(false);
                setDraft('');
              }}
            >
              {String(t('common.cancel'))}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 mt-3">
          {hasDspEntity ? (
            <NotifyDspButton
              warehouseId={warehouseId}
              alertId={alert.alertId}
              label={String(t('workbench.alertText.notifiedDsp'))}
              onNotified={onCommentSaved}
            />
          ) : (
            <Button
              size="small"
              type="primary"
              disabled={!warehouseId}
              onClick={() => setCommenting(true)}
            >
              {composeAlertHandleAction(alert)}
            </Button>
          )}
          {detailPath && (
            <Button size="small" onClick={() => navigate(detailPath)}>
              {String(t('workbench.alertText.viewDetail'))}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const PeriodReviewSection: React.FC<PeriodReviewSectionProps> = ({
  data,
  warehouseId,
  timeZone,
  loading,
  onCommentSaved,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('open');
  const paging = useHandledAlertPaging(warehouseId, 'PERIOD');

  if (loading) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  const openAlerts = data.alerts.filter((a) => !a.resolvedAt);
  const closedAlerts = paging.items ?? data.alerts.filter((a) => a.resolvedAt);
  const isOpen = activeTab === 'open';
  const visible = sortForTab(isOpen ? openAlerts : closedAlerts, isOpen);

  return (
    <div className="border border-violet-200 rounded-lg flex flex-col">
      <ColumnHeader
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          if (key === 'closed') paging.loadFirstPageOnce();
        }}
        tabs={[
          { key: 'open', label: String(t('workbench.periodReview')), count: openAlerts.length },
          { key: 'closed', label: String(t('workbench.closedReview')), count: data.handledCount },
        ]}
        extra={String(t('workbench.last7Days'))}
      />

      {/* min-w-min 的作用同 AlertsSection：不加则告警行的宽度需求被滚动容器吃掉 */}
      <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto min-w-min">
        {visible.length > 0 ? (
          visible.map((alert) => (
            <ReviewCard
              key={alert.alertId}
              alert={alert}
              warehouseId={warehouseId}
              timeZone={timeZone}
              onCommentSaved={onCommentSaved}
            />
          ))
        ) : (
          <Empty description={String(t('workbench.noPeriodReview'))} />
        )}

        {!isOpen && paging.hasMore && (
          <div className="text-center">
            <Button type="link" loading={paging.loading} onClick={paging.loadNextPage}>
              {String(t('workbench.loadMore'))}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PeriodReviewSection;
